import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

/**
 * Phase 2 — Promoter daily sale entry (mobile).
 * Submits a sale with status='pending_verification'. Invoice photo upload uses
 * the same dataURL pattern as the rest of the app.
 */
const initialForm = {
  sale_date: new Date().toISOString().slice(0, 10),
  customer_name: "", customer_phone: "", customer_address: "",
  dealer_id: "", dealer_name: "",
  brand: "", product_category: "", product_model: "",
  product_serial_number: "", quantity: 1,
  invoice_number: "", invoice_amount: "",
  invoice_date: new Date().toISOString().slice(0, 10),
  invoice_photo_url: "", remarks: "",
};

export default function PromoterSaleEntry() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [profile, setProfile] = useState(null);
  const [products, setProducts] = useState([]);   // shared CRM master
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    Promise.all([
      api.get("/promoter/me"),
      api.get("/products"),
    ]).then(([p, pr]) => {
      setProfile(p.data);
      setProducts(pr.data || []);
      setForm((f) => ({
        ...f,
        dealer_id: p.data.assigned_dealer_id || "",
        dealer_name: p.data.assigned_dealer_name || "",
      }));
    }).catch((e) => toast.error("Could not load", { description: formatApiErrorDetail(e.response?.data?.detail) }));
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Phase 4.6 — live VAT split preview. Promoters enter the invoice TOTAL (VAT-inclusive).
  // Targets/incentives use net sales (excl. VAT); we show the breakdown live so there
  // are no surprises at approval time. Rate is loaded from /promoter/settings/vat
  // (default 13%, env-configurable).
  const [vatRate, setVatRate] = useState(0.13);
  useEffect(() => {
    api.get("/promoter/settings/vat").then((r) => setVatRate(Number(r.data?.vat_rate) || 0.13)).catch(() => {});
  }, []);
  const grossAmount = Number(form.invoice_amount || 0);
  const netAmount = grossAmount > 0 ? grossAmount / (1 + vatRate) : 0;
  const vatAmount = grossAmount - netAmount;
  const fmt = (n) => (Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const vatPct = (vatRate * 100).toFixed(vatRate * 100 % 1 === 0 ? 0 : 2);

  const brands = Array.from(new Set(products.map((p) => p.brand).filter(Boolean))).sort();
  const categories = Array.from(new Set(
    products.filter((p) => !form.brand || p.brand === form.brand).map((p) => p.category).filter(Boolean)
  )).sort();
  const models = Array.from(new Set(
    products
      .filter((p) => (!form.brand || p.brand === form.brand) && (!form.product_category || p.category === form.product_category))
      .map((p) => p.model).filter(Boolean)
  )).sort();

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("Invoice photo too large (max 4 MB)"); return; }
    const r = new FileReader();
    r.onload = () => update("invoice_photo_url", r.result);
    r.onerror = () => toast.error("Could not read photo");
    r.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim()) { toast.error("Customer name is required"); return; }
    if (!form.customer_phone.trim()) { toast.error("Customer phone is required"); return; }
    if (!form.brand) { toast.error("Brand is required"); return; }
    if (!form.product_model) { toast.error("Product model is required"); return; }
    if (!form.invoice_number.trim()) { toast.error("Invoice number is required"); return; }
    if (form.invoice_amount === "" || Number.isNaN(Number(form.invoice_amount))) {
      toast.error("Invoice amount is required"); return;
    }
    setBusy(true);
    try {
      await api.post("/promoter/sales", {
        ...form,
        invoice_amount: Number(form.invoice_amount),
        quantity: Number(form.quantity || 1),
      });
      toast.success("Sale submitted", { description: "Awaiting verification" });
      navigate("/promoter/my-sales");
    } catch (err) {
      toast.error("Could not submit", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="sale-entry-form">
      <header>
        <h1 className="font-heading text-2xl font-bold text-slate-900">New Sale</h1>
        <p className="text-xs text-slate-500 mt-0.5">
          {profile?.assigned_dealer_name ? `Counter: ${profile.assigned_dealer_name}` : "Default dealer will be auto-filled if set."}
        </p>
      </header>

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <F label="Sale Date" required><Input type="date" value={form.sale_date} onChange={(e) => update("sale_date", e.target.value)} data-testid="sale-date" /></F>
            <F label="Quantity" required><Input type="number" min={1} value={form.quantity} onChange={(e) => update("quantity", e.target.value)} data-testid="sale-qty" /></F>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="text-sm font-semibold text-slate-900">Customer</div>
          <F label="Name" required><Input value={form.customer_name} onChange={(e) => update("customer_name", e.target.value)} data-testid="sale-customer-name" /></F>
          <F label="Phone" required><Input value={form.customer_phone} onChange={(e) => update("customer_phone", e.target.value)} data-testid="sale-customer-phone" /></F>
          <F label="Address (optional)"><Textarea rows={2} value={form.customer_address} onChange={(e) => update("customer_address", e.target.value)} data-testid="sale-customer-address" /></F>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="text-sm font-semibold text-slate-900">Product</div>
          <F label="Brand" required>
            <Select value={form.brand} onValueChange={(v) => setForm((f) => ({ ...f, brand: v, product_category: "", product_model: "" }))}>
              <SelectTrigger data-testid="sale-brand"><SelectValue placeholder="Select brand" /></SelectTrigger>
              <SelectContent>{brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Category">
            <Select value={form.product_category} onValueChange={(v) => setForm((f) => ({ ...f, product_category: v, product_model: "" }))}>
              <SelectTrigger data-testid="sale-category"><SelectValue placeholder={form.brand ? "Select category" : "Pick brand first"} /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Model" required>
            <Select value={form.product_model} onValueChange={(v) => update("product_model", v)}>
              <SelectTrigger data-testid="sale-model"><SelectValue placeholder={form.brand ? "Select model" : "Pick brand first"} /></SelectTrigger>
              <SelectContent>
                {models.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No models — type below</div>}
                {models.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
          <F label="Or enter model manually">
            <Input value={form.product_model} onChange={(e) => update("product_model", e.target.value)} placeholder="Manual model entry" data-testid="sale-model-manual" />
          </F>
          <F label="Serial # (optional)"><Input value={form.product_serial_number} onChange={(e) => update("product_serial_number", e.target.value)} data-testid="sale-serial" /></F>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="text-sm font-semibold text-slate-900">Invoice</div>
          <F label="Invoice #" required><Input value={form.invoice_number} onChange={(e) => update("invoice_number", e.target.value)} data-testid="sale-invoice-no" /></F>
          <div className="grid grid-cols-2 gap-2">
            <F label="Date" required><Input type="date" value={form.invoice_date} onChange={(e) => update("invoice_date", e.target.value)} data-testid="sale-invoice-date" /></F>
            <F label={`Invoice Total (incl. ${vatPct}% VAT)`} required>
              <Input type="number" min={0} step="0.01" value={form.invoice_amount}
                     onChange={(e) => update("invoice_amount", e.target.value)} data-testid="sale-invoice-amount" />
            </F>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded p-2 text-xs space-y-0.5"
               data-testid="sale-vat-preview">
            <div className="flex justify-between text-slate-600">
              <span>Invoice gross (incl. VAT)</span>
              <span className="font-mono">Rs. {fmt(grossAmount)}</span>
            </div>
            <div className="flex justify-between text-slate-600">
              <span>VAT @ {vatPct}%</span>
              <span className="font-mono" data-testid="sale-vat-amount">Rs. {fmt(vatAmount)}</span>
            </div>
            <div className="flex justify-between font-semibold text-slate-900 pt-0.5 border-t border-slate-200 mt-0.5">
              <span>Net sales (excl. VAT)</span>
              <span className="font-mono" data-testid="sale-net-amount">Rs. {fmt(netAmount)}</span>
            </div>
            <div className="text-[10px] text-slate-500 pt-0.5">
              Targets and incentive achievement use net sales only.
            </div>
          </div>
          <F label="Invoice photo (optional but recommended)">
            {form.invoice_photo_url ? (
              <div className="flex items-start gap-2">
                <img src={form.invoice_photo_url} alt="invoice" className="w-20 h-20 rounded border object-cover" />
                <button type="button" onClick={() => update("invoice_photo_url", "")}
                        className="text-xs text-rose-600 hover:underline" data-testid="sale-photo-remove">Remove</button>
              </div>
            ) : (
              <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}
                      className="w-full" data-testid="sale-photo-btn">
                <Camera size={14} className="mr-1" /> Upload invoice photo
              </Button>
            )}
            <input ref={fileInputRef} type="file" accept="image/*" capture="environment"
                   onChange={onPhoto} className="hidden" data-testid="sale-photo-input" />
          </F>
          <F label="Remarks (optional)">
            <Textarea rows={2} value={form.remarks} onChange={(e) => update("remarks", e.target.value)} data-testid="sale-remarks" />
          </F>
        </CardContent>
      </Card>

      <Button type="submit" disabled={busy}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="sale-submit-btn">
        <ShoppingBag size={14} className="mr-1" /> {busy ? "Submitting..." : "Submit sale"}
      </Button>
    </form>
  );
}

function F({ label, required, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] uppercase font-semibold text-slate-600">
        {label}{required && <span className="text-rose-600 ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}
